import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { termsVersions } from '@practist/components';
import TermsModal from '../legal/TermsModal';
import { AuthenticatedSession } from '@/authenticatedSession/authenticatedSession';

interface TermsContextProps {
  hasAcceptedLatestTerms: boolean;
}

interface TermsProviderProps {
  authSession: AuthenticatedSession;
  children: ReactNode;
}

const TermsContext = createContext<TermsContextProps>({
  hasAcceptedLatestTerms: true,
});

export const TermsProvider: React.FC<TermsProviderProps> = ({
  authSession,
  children,
}) => {
  const [modalData, setModalData] = useState<{
    versionData: (typeof termsVersions)[number];
    showChangelog: boolean;
  } | null>(null);

  useEffect(() => {
    const checkTerms = async () => {
      try {
        const latestVersion = termsVersions[termsVersions.length - 1];
        const userSnapshot = await authSession.db.getUser(
          authSession.userIds.firestore,
        );
        if (!userSnapshot.exists) {
          return;
        }

        const userLatestVersion = userSnapshot.data()?.latestAcceptedVersion;

        if (userLatestVersion !== latestVersion.versionNumber) {
          setModalData({
            versionData: latestVersion,
            showChangelog: !!userLatestVersion,
          });
        }
      } catch (error) {
        console.error('Error checking terms:', error);
      }
    };

    checkTerms();
  }, [authSession.userIds.firestore, authSession.tenantId]);

  const handleAccept = async () => {
    if (modalData) {
      try {
        await authSession.db.updateUser(authSession.userIds.firestore, {
          latestAcceptedVersion: modalData.versionData.versionNumber,
        });
        setModalData(null); // Closes the modal
      } catch (error) {
        console.error('Error updating terms acceptance:', error);
      }
    }
  };

  return (
    <TermsContext.Provider value={{ hasAcceptedLatestTerms: !modalData }}>
      {children}
      {modalData && (
        <TermsModal
          versionData={modalData.versionData}
          onAccept={handleAccept}
        />
      )}
    </TermsContext.Provider>
  );
};

export const useTermsContext = () => useContext(TermsContext);
